import { Suspense } from 'react'
import { isMobile } from 'react-device-detect'
import { createPortal } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { pipe } from 'remeda'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import { WishlistDrawer } from '@components/WishlistDrawer/WishlistDrawer'
import { lazyImport } from '@helpers'
import {
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import type { CartDrawerData, SizeGuideSection } from '@velasca/types'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { productMedia } from '@velasca/utils/globals'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'
import { transformProductMedia } from '@velasca/utils/transform-product-media'
import { transformProductPageData } from '@velasca/utils/transform-product-page-data'
import { withWishlist } from '../hocs/withWishlist'
import { getBreadcrumbs } from './FooterApp'

const { ApparelProductPage } = lazyImport(
  () => import('@components/ApparelProductPage/ApparelProductPage'),
  'ApparelProductPage',
)

const apparelProductEl = document.getElementById('apparel-product')
const apparelProductJson = document.getElementById('apparel-product-json')
const productMoreDetailsEl = document.getElementById(
  'product-more-details-json',
)
const productMaterialEl = document.getElementById('product-material-json')
const productConstructionEl = document.getElementById(
  'product-construction-json',
)
const productFeaturedDetailEl = document.getElementById('featured-detail-json')
const productContentlEl = document.getElementById('product-content-json')
const productCareEl = document.getElementById('product-care-json')
const productInstructionsEl = document.getElementById(
  'product-instructions-json',
)
const productMadeInItalyEl = document.getElementById(
  'product-made-in-italy-json',
)
const productManufacturingEl = document.getElementById(
  'product-manufacturing-json',
)
const productCollectionBrandEl = document.getElementById(
  'product-collection-brand-json',
)
const complementaryProductsSettingsEl = document.getElementById(
  'complementary-products-settings-json',
)
const complementaryProductsSettingsJson = complementaryProductsSettingsEl
  ? parseJSONSafe(complementaryProductsSettingsEl.innerText)
  : {}

const productSizeGuideEl = document.querySelector<HTMLElement>(
  '#apparel-size-guide-json',
)
const productPackagingEl = document.querySelector<HTMLElement>(
  '#product-packaging-json',
)
const productSizeGuideJson: SizeGuideSection = productSizeGuideEl
  ? parseJSONSafe(productSizeGuideEl.innerText)
  : {}

export const renderApparelProductPageApp = (
  cartDrawerData: CartDrawerData,
  headerEl: HTMLElement | null,
  headerData: any,
  relatedProductsSettingsJson: any,
  recentProductsSettingsJson: any,
  promotionalBannerData: any,
  minisearchData: any,
) => {
  if (apparelProductEl && apparelProductJson) {
    const breadcrumbs = getBreadcrumbs(headerData?.nav)

    const ApparelProductPageApp = () => (
      <>
        <CartDrawer
          emptyCartCtas={cartDrawerData?.ctas || []}
          editorialSuggestedProductsDesktopImage={
            cartDrawerData?.editorial?.suggestedProductsDesktopImage
          }
        />
        <WishlistDrawer />
        {headerEl &&
          createPortal(
            <Header
              sticky
              promotionalBanner={promotionalBannerData || {}}
              miscHeaderData={headerData?.miscData}
              minisearch={minisearchData}
              lowerMenu={headerData?.mobileOnlyNav}
              menu={
                renderLiquidLinks(
                  headerData?.nav,
                  headerData?.miscData?.liquidLinks,
                ) ?? undefined
              }
            />,
            headerEl,
          )}
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={null}>
            <ApparelProductPage
              breadcrumbs={breadcrumbs}
              complementaryProductSettings={complementaryProductsSettingsJson}
              relatedProductsSettings={relatedProductsSettingsJson}
              recentProductsSettings={recentProductsSettingsJson}
              productMedia={transformProductMedia(
                productMedia as any,
                isMobile,
              )}
              product={transformProductPageData(
                parseJSONSafe(apparelProductJson.innerText),
              )}
              moreDetailsInfo={
                productMoreDetailsEl?.innerText
                  ? parseJSONSafe(productMoreDetailsEl.innerText)
                  : false
              }
              materialSection={
                productMaterialEl?.innerText
                  ? parseJSONSafe(productMaterialEl.innerText)
                  : false
              }
              constructionSection={
                productConstructionEl?.innerText
                  ? parseJSONSafe(productConstructionEl.innerText)
                  : false
              }
              featuredDetailSection={
                productFeaturedDetailEl?.innerText
                  ? parseJSONSafe(productFeaturedDetailEl.innerText)
                  : false
              }
              contentKitSection={
                productContentlEl?.innerText
                  ? parseJSONSafe(productContentlEl.innerText)
                  : false
              }
              careSection={
                productCareEl?.innerText
                  ? parseJSONSafe(productCareEl.innerText)
                  : false
              }
              productInstructions={
                productInstructionsEl?.innerText
                  ? parseJSONSafe(productInstructionsEl.innerText)
                  : false
              }
              madeInItalySection={
                productMadeInItalyEl?.innerText
                  ? parseJSONSafe(productMadeInItalyEl.innerText)
                  : false
              }
              manufacturingSection={
                productManufacturingEl?.innerText
                  ? parseJSONSafe(productManufacturingEl.innerText)
                  : false
              }
              packagingSection={
                productPackagingEl?.innerText
                  ? parseJSONSafe(productPackagingEl.innerText)
                  : false
              }
              collectionBrandSection={
                productCollectionBrandEl?.innerText
                  ? parseJSONSafe(productCollectionBrandEl.innerText)
                  : false
              }
              sizeGuideSection={
                productSizeGuideEl?.innerText ? productSizeGuideJson : undefined
              }
            />
          </Suspense>
        </ErrorBoundary>
      </>
    )

    const ApparelProductPageAppWrapper = pipe(
      ApparelProductPageApp,
      withStaffPickImage,
      withExchangeRates,
      withWishlist,
      withI18next,
      withApollo,
      withErrorBoundary,
      withTheme,
    )

    ReactDOM.createRoot(apparelProductEl).render(
      <ApparelProductPageAppWrapper />,
    )
  }
}
